import { useEffect, useState } from "react";
import { LETTERS_PATH, LogoMark, MARK_INNER, STAR_PATH } from "@/components/brand/Logo";

/*
 * The one loading state across the site and the app. It tells the brand's
 * story in one loop: the star from the logo leaves a departure point, flies a
 * dotted route and lands as the dot on the "i", the goal you are moving
 * towards. The tile nods as it lands, the star lights up and the route fades
 * before the next flight.
 *
 * SVG with SMIL timing, so it runs before any animation library has loaded and
 * in every browser. People who ask for reduced motion see the still mark.
 */
const LOOP = "2.8s";
// Departure, take-off, landing, end of loop (fractions of LOOP).
const T = "0;0.12;0.66;1";
const EASE = "0 0 1 1;0.6 0 0.3 1;0 0 1 1";
const ROUTE = "M18 78 C 40 -4, 150 -30, 169.1 37.5";
const INK = "#0A0F0E";
const TEAL = "#2EE6D6";
const TEAL_DEEP = "#00786F";
// The star's own centre in the mark, so it can fly from its middle.
const STAR_CENTRE = "translate(-53.36 -18.06)";

function useReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const q = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    if (!q) return;
    setReduced(q.matches);
    const on = () => setReduced(q.matches);
    q.addEventListener?.("change", on);
    return () => q.removeEventListener?.("change", on);
  }, []);
  return reduced;
}

function Flight() {
  return (
    <svg viewBox="0 -10 196 106" width={176} height={95} aria-hidden="true" focusable="false" className="overflow-visible">
      <defs>
        <mask id="loader-route-drawn" maskUnits="userSpaceOnUse">
          <path d={ROUTE} fill="none" stroke="#fff" strokeWidth="6" pathLength={1} strokeDasharray="1 1">
            <animate attributeName="stroke-dashoffset" values="1;1;0;0" keyTimes={T} calcMode="spline" keySplines={EASE} dur={LOOP} repeatCount="indefinite" />
          </path>
        </mask>
      </defs>

      {/* Departure point. */}
      <circle cx="18" cy="78" r="4" fill="none" stroke={TEAL_DEEP} strokeWidth="2" />

      {/* The route, drawn behind the star, gone before the next flight. */}
      <g mask="url(#loader-route-drawn)">
        <path d={ROUTE} fill="none" stroke={TEAL_DEEP} strokeWidth="2" strokeLinecap="round" strokeDasharray="0.1 7" />
        <animate attributeName="opacity" values="1;1;0;0" keyTimes="0;0.74;0.92;1" dur={LOOP} repeatCount="indefinite" />
      </g>

      {/* The tile. It nods when the star lands. */}
      <g>
        <animateTransform attributeName="transform" type="translate" values="120 16;120 16;120 18;120 16;120 16" keyTimes="0;0.66;0.7;0.78;1" dur={LOOP} repeatCount="indefinite" />
        <rect width="64" height="64" rx="18" fill={INK} />
        <g transform={MARK_INNER}>
          <path d={LETTERS_PATH} fill="#FFFFFF" />
          {/* The destination: a faint star that lights up on arrival. */}
          <path d={STAR_PATH} fill={TEAL}>
            <animate attributeName="opacity" values="0.22;0.22;1;1;0.22" keyTimes="0;0.66;0.69;0.86;1" dur={LOOP} repeatCount="indefinite" />
          </path>
        </g>
      </g>

      {/* A ripple where it lands. */}
      <circle cx="169.1" cy="37.5" r="4" fill="none" stroke={TEAL} strokeWidth="2" opacity="0">
        <animate attributeName="r" values="4;4;16;16" keyTimes="0;0.66;0.84;1" dur={LOOP} repeatCount="indefinite" />
        <animate attributeName="opacity" values="0;0;0.8;0;0" keyTimes="0;0.66;0.68;0.84;1" dur={LOOP} repeatCount="indefinite" />
      </circle>

      {/* The star in flight. */}
      <g>
        <animateMotion path={ROUTE} keyPoints="0;0;1;1" keyTimes={T} calcMode="spline" keySplines={EASE} dur={LOOP} repeatCount="indefinite" />
        <g>
          <animateTransform attributeName="transform" type="rotate" values="-40;-40;0;0" keyTimes={T} calcMode="spline" keySplines={EASE} dur={LOOP} repeatCount="indefinite" />
          {/* Bigger in the air, the logo's own size on landing. */}
          <animateTransform attributeName="transform" type="scale" values="1.5;1.5;1;1" keyTimes={T} calcMode="spline" keySplines={EASE} dur={LOOP} repeatCount="indefinite" additive="sum" />
          <g transform={`scale(0.8) ${STAR_CENTRE}`}>
            <path d={STAR_PATH}>
              <animate attributeName="fill" values={`${TEAL_DEEP};${TEAL_DEEP};${TEAL};${TEAL}`} keyTimes="0;0.5;0.62;1" dur={LOOP} repeatCount="indefinite" />
            </path>
          </g>
        </g>
        <animate attributeName="opacity" values="0;1;1;0;0" keyTimes="0;0.08;0.67;0.71;1" dur={LOOP} repeatCount="indefinite" />
      </g>
    </svg>
  );
}

export function Loader({ label, fullScreen = false }: { label?: string; fullScreen?: boolean }) {
  const reduced = useReducedMotion();
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    const t = window.setTimeout(() => setSlow(true), 6000);
    return () => window.clearTimeout(t);
  }, []);

  return (
    <div
      role="status"
      aria-live="polite"
      className={`flex flex-col items-center justify-center gap-4 ${fullScreen ? "min-h-[100svh]" : "min-h-[60vh]"}`}
      style={{ backgroundColor: fullScreen ? "#FBFBF8" : undefined }}
    >
      {reduced ? <LogoMark size={56} /> : <Flight />}
      <span className="h-5 text-[15px] font-semibold" style={{ color: "#46504D", animation: "loader-in 400ms ease both" }}>
        {slow ? "Still working. A slow connection, most likely." : label ?? ""}
      </span>
    </div>
  );
}
